import { useState, useCallback } from 'react';
import { Link } from '@inertiajs/react';
import { route } from '../../utils/route';
import type { ProductListing } from '../../types/products';

interface CompactProductCardProps {
  product: ProductListing;
  onAddToCart: (productId: number, formatId: number, quantity: number) => Promise<void>;
}

function formatPrice(price: number): string {
  return price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function CompactProductCard({ product, onAddToCart }: CompactProductCardProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [added, setAdded] = useState(false);

  const format = product.default_format ?? product.formats[0] ?? null;
  const detailUrl = product.slug
    ? route('product.show', { slug: product.slug })
    : `/producto/${product.id}`;

  const handleAdd = useCallback(async () => {
    if (!format || isAdding) return;
    setIsAdding(true);
    try {
      await onAddToCart(product.id, format.id, 1);
      setAdded(true);
      setTimeout(() => setAdded(false), 2000);
    } catch {
      // handled upstream
    } finally {
      setIsAdding(false);
    }
  }, [format, isAdding, product.id, onAddToCart]);

  if (!format) return null;

  return (
    <div className="flex flex-1 flex-col rounded-lg border border-gray-200 bg-white p-2.5 sm:p-3">
      <Link href={detailUrl} className="mx-auto mb-2 block">
        {product.has_image ? (
          <img
            src={product.webp_image_url}
            alt={product.title}
            loading="lazy"
            width={100}
            height={100}
            className="aspect-square w-[80px] sm:w-[100px] object-contain"
          />
        ) : (
          <div className="flex aspect-square w-[80px] sm:w-[100px] items-center justify-center rounded bg-gray-100">
            <i className="fa-solid fa-leaf text-2xl text-gray-300" />
          </div>
        )}
      </Link>

      <Link
        href={detailUrl}
        className="mb-1 text-xs sm:text-sm font-medium leading-tight text-gray-800 hover:text-green-700 no-underline line-clamp-2"
      >
        {product.title}
      </Link>

      <div className="mt-auto mb-2">
        {product.formats.length > 1 && (
          <div className="text-[11px] text-gray-400 truncate">{format.name}</div>
        )}
        <div className="text-sm font-bold text-gray-800">
          ${formatPrice(format.discounted_price)}
        </div>
      </div>

      <button
        type="button"
        onClick={handleAdd}
        disabled={isAdding}
        className={`w-full rounded-lg px-2 py-2 text-xs sm:text-sm font-medium transition-colors ${
          added
            ? 'bg-green-600 text-white'
            : 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800'
        }`}
      >
        {isAdding ? (
          <i className="fa-solid fa-spinner fa-spin" />
        ) : added ? (
          <span className="flex items-center justify-center gap-1">
            <i className="fa-solid fa-check" /> Agregado
          </span>
        ) : (
          'Agregar'
        )}
      </button>
    </div>
  );
}
